import { twMerge } from 'tailwind-merge';
import type { ParsedValue, ParsedClasses } from './types';
import { EDITOR_BREAKPOINTS } from './types';
import { getPrefixForProperty, getAllPrefixes } from './propertyMap';
import type { TailwindProperty } from './propertyMap';

export function mergeTailwindClasses(...classStrings: string[]): string {
  return twMerge(classStrings.join(' '));
}

// Cache for standalone class names (classes that are prefixes themselves, like flex, block, flex-row)
const standaloneClassSet = new Set(getAllPrefixes());

export function buildValue(value: ParsedValue, options?: { includeBreakpoint?: boolean }): string {
  const { property, type, modifier, breakpoint } = value;
  const prefix = getPrefixForProperty(property);
  const includeBreakpoint = options?.includeBreakpoint ?? true;

  let className: string;

  // Check if this is a standalone class (where value IS the full class name)
  if (type === 'standard' && value.value && standaloneClassSet.has(value.value)) {
    className = value.value;
  } else if (!prefix) {
    // Fallback: if we can't find the prefix, return empty string
    return '';
  } else if (type === 'arbitrary') {
    if (value.value) {
      className = `${prefix}-[${value.value}]`;
    } else {
      className = prefix;
    }
  } else if (value.value) {
    className = `${prefix}-${value.value}`;
  } else {
    className = prefix;
  }

  if (modifier) {
    className = `${className}/${modifier}`;
  }

  // Add breakpoint prefix for non-base breakpoints (unless disabled)
  if (includeBreakpoint && breakpoint && breakpoint !== 'base') {
    className = `${breakpoint}:${className}`;
  }

  return className;
}

export function buildClasses(parsed: ParsedClasses): string {
  const classes: string[] = [];
  const breakpoints = EDITOR_BREAKPOINTS;

  for (const [property, propertyValue] of Object.entries(parsed)) {
    if (property !== 'unknown' && typeof propertyValue === 'object' && !Array.isArray(propertyValue)) {
      for (const breakpoint of breakpoints) {
        const value = propertyValue[breakpoint];
        if (value) {
          // buildValue now handles the breakpoint prefix
          classes.push(buildValue(value));
        }
      }
    }
  }

  if (parsed.unknown && parsed.unknown.length > 0) {
    classes.push(...parsed.unknown);
  }

  return mergeTailwindClasses(classes.join(' '));
}

/**
 * A simple property → class string mapping without breakpoint nesting.
 * Used when you only need base values, not breakpoint-specific overrides.
 *
 * @example { paddingX: 'px-4', backgroundColor: 'bg-primary', unknownClasses: ['relative'] }
 */
export type TailwindClassMap = {
  [K in TailwindProperty]?: string;
} & {
  /** Classes that couldn't be parsed into known properties */
  unknownClasses?: string[];
};

/**
 * Resolve a parsed class structure to just its base (mobile-first) values.
 * Responsive overrides (sm:/md:/lg:/xl:/2xl:) are preserved in unknownClasses.
 *
 * Use this when you need a simple property → value mapping without breakpoint awareness.
 *
 * @example
 * resolveToBaseClasses(parseClasses('px-4 lg:px-8 relative'));
 * // Returns: { paddingX: 'px-4', unknownClasses: ['lg:px-8', 'relative'] }
 */
export function resolveToBaseClasses(parsed: ParsedClasses): TailwindClassMap {
  const result: TailwindClassMap = {};
  const unknownClasses: string[] = [];

  for (const [property, propertyValue] of Object.entries(parsed)) {
    if (property === 'unknown') {
      if (Array.isArray(propertyValue)) {
        unknownClasses.push(...propertyValue);
      }
    } else if (typeof propertyValue === 'object' && !Array.isArray(propertyValue)) {
      // Use base value for the property
      if (propertyValue.base) {
        (result as Record<string, string>)[property] = buildValue(propertyValue.base, { includeBreakpoint: false });
      }
      // Put responsive values into unknownClasses (preserve responsive classes)
      for (const breakpoint of EDITOR_BREAKPOINTS) {
        if (breakpoint !== 'base') {
          const value = propertyValue[breakpoint];
          if (value) {
            unknownClasses.push(buildValue(value, { includeBreakpoint: true }));
          }
        }
      }
    }
  }

  if (unknownClasses.length > 0) {
    result.unknownClasses = unknownClasses;
  }

  return result;
}

/**
 * Convert a ClassName object back to a Tailwind class string.
 *
 * @example
 * classesToString({ paddingX: 'px-4', backgroundColor: 'bg-primary', unknownClasses: ['relative'] });
 * // Returns: 'px-4 bg-primary relative'
 */
export function classesToString(classes: TailwindClassMap): string {
  const classStrings: string[] = [];

  for (const [key, value] of Object.entries(classes)) {
    if (key !== 'unknownClasses' && value && typeof value === 'string') {
      classStrings.push(value);
    }
  }

  if (classes.unknownClasses && classes.unknownClasses.length > 0) {
    classStrings.push(...classes.unknownClasses);
  }

  return classStrings.join(' ');
}
